import React, { lazy, Suspense, useEffect } from 'react';
import proptypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { normalizePageType, getAiPromptLocation } from '../../AddToHelpers';
import { AddToProvider } from '../../AddToContext';

// Import only the dataModel for extend() - component is lazy loaded
import { AIListBuilder as AIListBuilderModule } from './AIListBuilder';

const AIListBuilder = lazy(() => (
  import('./AIListBuilder').then((module) => ({
    default: module.AIListBuilder
  }))
));

const { string, func } = proptypes;

// Shared by AddToListSuccess and AddToProjectSuccess: captures a prompt into
// sessionStorage for the destination page to auto-generate on landing, and fires
// the addToMaterialsOverlay analytics event either way (with or without the prompt).
const AiPromptSuccessSection = ({
  addType,
  entityId,
  entityIdField,
  redirectUrl,
  title,
  description
}) => {
  const aiSuccessScreenEnabled = useConfigService('fs:aiListBuilderSuccessScreenEnabled');
  const showAiPrompt = !!aiSuccessScreenEnabled;

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to.addToMaterialsOverlay', {
      addType,
      ...(showAiPrompt && { aiPromptLocation: getAiPromptLocation() })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showAiPrompt) {
    return null;
  }

  const handleAiGenerate = (prompt) => {
    const rawPageType = typeof window !== 'undefined'
      ? window.digitalData?.page?.pageInfo?.pageType
      : null;
    const pageType = normalizePageType(rawPageType);

    try {
      THDStorageUtils.set(SESSION_STORAGE, {
        key: `lbai_prompt_${entityId}`,
        value: JSON.stringify({ prompt, [entityIdField]: entityId, pageType })
      });
    } catch (err) {
      // Graceful degradation: navigate anyway, just without auto-fill
    }
    window.location.href = redirectUrl;
  };

  return (
    <AddToProvider>
      <Suspense fallback={null}>
        <div className="sui-mt-4">
          <AIListBuilder
            onGenerate={handleAiGenerate}
            title={title}
            description={description}
          />
        </div>
      </Suspense>
    </AddToProvider>
  );
};

AiPromptSuccessSection.propTypes = {
  addType: string.isRequired,
  entityId: string.isRequired,
  entityIdField: string.isRequired,
  redirectUrl: string.isRequired,
  title: string.isRequired,
  description: string.isRequired
};

AiPromptSuccessSection.displayName = 'AiPromptSuccessSection';

AiPromptSuccessSection.dataModel = extend(AIListBuilderModule.dataModel);

export { AiPromptSuccessSection };
